import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import AES from "crypto-js/aes";
import userTypeConsts from "../../constants/userTypeConsts";
import DownloadImage from "../../images/icons/download-icon.svg";
import SearchIcon from "../../images/icons/search-icon.svg";
import "./MvpClientTable.css";

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || "encryptionKey";
const PAGE_SIZE_OPTIONS = [6, 10, 25];

function personName(person) {
  if (!person || typeof person !== "object") return "";
  const personal = [person.first_name, person.middle_name, person.last_name]
    .filter((part) => part && part !== "null")
    .join(" ")
    .trim();
  if (personal) return personal;
  const company = [person.company_first_name, person.company_last_name]
    .filter((part) => part && part !== "null")
    .join(" ")
    .trim();
  return company || (person.company_name && person.company_name !== "null" ? person.company_name : "");
}

function contactName(item) {
  return personName(item.contact_point) || personName(item.acc_manager) || "—";
}

function contactEmail(item) {
  const email = item.email && item.email !== "null" ? item.email.trim() : "";
  return email || "—";
}

function pageItems(current, total) {
  if (total <= 5) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }
  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) items.push("ellipsis-start");
  for (let page = start; page <= end; page += 1) items.push(page);
  if (end < total - 1) items.push("ellipsis-end");
  items.push(total);
  return items;
}

function HoverText({ text }) {
  const clipRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const value = text || "—";

  useEffect(() => {
    const el = clipRef.current;
    if (!el) return undefined;
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [value]);

  return (
    <span className="mvp-hover-text">
      <span ref={clipRef} className="mvp-hover-text-clip">{value}</span>
      {truncated && <span className="mvp-hover-text-full">{value}</span>}
    </span>
  );
}

function SortIcon() {
  return (
    <span className="mvp-sort-icon" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none">
        <path d="M0.424316 3.64275L3.21843 0.848633L6.01255 3.64275" strokeWidth="1.2" stroke="#405BFF" />
      </svg>
      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="5" viewBox="0 0 7 5" fill="none">
        <path d="M6.0127 0.424632L3.21858 3.21875L0.42446 0.424632" strokeWidth="1.2" stroke="#405BFF" />
      </svg>
    </span>
  );
}

function CompanyMark({ item }) {
  const logo = item.company_logo && item.company_logo !== "null" ? item.company_logo : "";
  const [failed, setFailed] = useState(false);

  if (!logo || failed) {
    return (
      <span className="mvp-company-mark" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M8 1.6 8.7 3.2l1.7.15-1.3 1.1.4 1.65L8 5.25 6.5 6.1l.4-1.65-1.3-1.1 1.7-.15L8 1.6Z"
            fill="#8E96A8"
          />
          <path
            d="M8 6.7a3.15 3.15 0 1 0 0 6.3 3.15 3.15 0 0 0 0-6.3Z"
            stroke="#8E96A8"
            strokeWidth="1.15"
          />
          <circle cx="8" cy="9.85" r="1.05" fill="#8E96A8" />
        </svg>
      </span>
    );
  }

  return (
    <img
      className="mvp-company-logo"
      src={`/uploads/clientCompyLogo/lowQuality/${logo.replace(/[^a-zA-Z0-9._-]/g, "")}`}
      alt=""
      onError={() => setFailed(true)}
    />
  );
}

export default function MvpClientTable({
  clientProfiles,
  isLoading,
  filter,
  setFilter,
  selectedStatus,
  setSelectedStatus,
  user,
  onNewClient,
  onExport,
  onBulkImport,
  isExportFetching,
}) {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState("client_name");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);
  const [statusOpen, setStatusOpen] = useState(false);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const statusRef = useRef(null);
  const pageSizeRef = useRef(null);
  const tableRef = useRef(null);

  const canManage =
    user &&
    (user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN);

  useEffect(() => {
    const close = (event) => {
      if (statusRef.current && !statusRef.current.contains(event.target)) setStatusOpen(false);
      if (pageSizeRef.current && !pageSizeRef.current.contains(event.target)) setPageSizeOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const rows = useMemo(() => {
    const search = (filter || "").trim().toLowerCase();
    const filtered = (clientProfiles || []).filter((item) => {
      if (selectedStatus === "Active" && !item.is_active) return false;
      if (selectedStatus === "Inactive" && item.is_active) return false;
      if (!search) return true;
      const haystack = [
        item.client_name,
        item.legal_entity_name,
        contactName(item),
        contactEmail(item),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(search);
    });

    const direction = sortDir === "asc" ? 1 : -1;
    return filtered.slice().sort((a, b) => {
      if (sortField === "created_at") {
        return (new Date(a.created_at) - new Date(b.created_at)) * direction;
      }
      if (sortField === "is_active") {
        return (Number(!!a.is_active) - Number(!!b.is_active)) * direction;
      }
      const left =
        sortField === "contact"
          ? contactName(a)
          : sortField === "email"
            ? contactEmail(a)
            : a.client_name || "";
      const right =
        sortField === "contact"
          ? contactName(b)
          : sortField === "email"
            ? contactEmail(b)
            : b.client_name || "";
      return left.localeCompare(right) * direction;
    });
  }, [clientProfiles, filter, selectedStatus, sortField, sortDir]);

  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * pageSize;
  const visible = rows.slice(start, start + pageSize);

  useEffect(() => {
    setPage(1);
  }, [filter, selectedStatus, pageSize, sortField, sortDir]);

  useEffect(() => {
    const tableEl = tableRef.current;
    const contentOuter = document.querySelector(".content-outer.clients");
    if (!tableEl || !contentOuter) return undefined;

    let atEnd = false;
    const updatePadding = () => {
      if (window.innerWidth > 767) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
        return;
      }
      const maxScroll = Math.max(0, tableEl.scrollWidth - tableEl.clientWidth);
      if (maxScroll <= 2) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
        return;
      }
      if (!atEnd && tableEl.scrollLeft >= maxScroll - 4) {
        atEnd = true;
        contentOuter.classList.add("mvp-hscroll-end");
      } else if (atEnd && tableEl.scrollLeft < maxScroll - 24) {
        atEnd = false;
        contentOuter.classList.remove("mvp-hscroll-end");
      }
    };

    tableEl.addEventListener("scroll", updatePadding, { passive: true });
    window.addEventListener("resize", updatePadding);
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updatePadding) : null;
    resizeObserver?.observe(tableEl);
    updatePadding();

    return () => {
      tableEl.removeEventListener("scroll", updatePadding);
      window.removeEventListener("resize", updatePadding);
      resizeObserver?.disconnect();
      contentOuter.classList.remove("mvp-hscroll-end");
    };
  }, [visible.length, isLoading]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDir(field === "created_at" ? "desc" : "asc");
  };

  const openDetails = (item) => {
    const encrypted = AES.encrypt(item._id, ENCRYPTION_KEY).toString().replace(/\//g, "%2F");
    navigate(`/client/details/${encodeURIComponent(encrypted)}`);
  };

  const statusLabel = selectedStatus || "All";

  return (
    <div className="mvp-client-table">
      <h2 className="mvp-mobile-heading">Clients</h2>
      <div className="mvp-client-toolbar">
        <div className="mvp-client-actions">
          {canManage && (
            <button type="button" className="mvp-client-btn mvp-client-btn-primary" onClick={onNewClient}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M7.75 0V15.5" stroke-width="1.5" stroke="white" />
                <path d="M15.5 7.75L0 7.75" stroke-width="1.5" stroke="white" />
              </svg>
              New Client
            </button>
          )}
          <button
            type="button"
            className="mvp-client-btn"
            onClick={onExport}
            disabled={isExportFetching}
          >
            <span className="mvp-site-icon" style={{ "--icon": `url(${DownloadImage})` }} aria-hidden="true" />
            {isExportFetching ? "Exporting…" : "Export"}
          </button>
          {canManage && (
            <button type="button" className="mvp-client-btn" onClick={onBulkImport}>
              <span className="mvp-site-icon mvp-site-icon-import" style={{ "--icon": `url(${DownloadImage})` }} aria-hidden="true" />
              Bulk Import
            </button>
          )}
        </div>
        <label className="mvp-client-search">
          <input
            type="text"
            value={filter}
            placeholder="Search"
            onChange={(event) => setFilter(event.target.value)}
            aria-label="Search clients"
            style={{ backgroundImage: `url(${SearchIcon})` }}
          />
        </label>
      </div>

      <div className="mvp-client-filters">
        <div className="mvp-status" ref={statusRef}>
          <span className="mvp-field-label">Status</span>
          <button
            type="button"
            className="mvp-status-trigger"
            aria-expanded={statusOpen}
            onClick={() => setStatusOpen((open) => !open)}
          >
            {statusLabel}
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
              <path d="M0.404297 0.444336L5.9043 5.44434L11.4043 0.444336" stroke-width="1.2" stroke="#405BFF" />
            </svg>
          </button>
          {statusOpen && (
            <ul className="mvp-menu">
              {["All", "Active", "Inactive"].map((option) => (
                <li key={option}>
                  <button
                    type="button"
                    className={statusLabel === option ? "is-active" : ""}
                    onClick={() => {
                      setSelectedStatus(option === "All" ? null : option);
                      setStatusOpen(false);
                    }}
                  >
                    {option}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mvp-result-count">
          <span>Showing</span>
          <div className="mvp-page-size" ref={pageSizeRef}>
            <button
              type="button"
              className="mvp-page-size-trigger"
              aria-label="Rows per page"
              onClick={() => setPageSizeOpen((open) => !open)}
            >
              {pageSize}
              <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true">
                <path d="M2.4 4.4 6 8l3.6-3.6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </button>
            {pageSizeOpen && (
              <ul className="mvp-menu mvp-menu-compact">
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <li key={size}>
                    <button
                      type="button"
                      className={pageSize === size ? "is-active" : ""}
                      onClick={() => {
                        setPageSize(size);
                        setPageSizeOpen(false);
                      }}
                    >
                      {size}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <span>of {total} results</span>
        </div>
      </div>

      <div className="mvp-table-card" ref={tableRef}>
        <table>
          <thead>
            <tr>
              {[
                ["client_name", "Company Name", "mvp-col-company"],
                ["created_at", "Customer Since", "mvp-col-since"],
                ["contact", "Point of Contact", "mvp-col-contact"],
                ["email", "Contact Email", "mvp-col-email"],
                ["is_active", "Status", "mvp-col-status"],
              ].map(([field, label, columnClass]) => (
                <th key={field} className={`${columnClass}${field === "client_name" ? " sticky-col" : ""}`}>
                  <button type="button" className="mvp-sort-btn" onClick={() => toggleSort(field)}>
                    {label}
                    <SortIcon />
                  </button>
                </th>
              ))}
              <th className="mvp-col-details" aria-label="Details" />
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: pageSize }).map((_, index) => (
                <tr key={`loading-${index}`} className="mvp-loading-row">
                  <td colSpan={6}>
                    <span className="mvp-loading-bar" />
                  </td>
                </tr>
              ))}
            {!isLoading && visible.length === 0 && (
              <tr>
                <td colSpan={6} className="mvp-empty">
                  No clients match this view.
                </td>
              </tr>
            )}
            {!isLoading &&
              visible.map((item) => (
                <tr key={item._id}>
                  <td className="mvp-col-company sticky-col">
                    <button type="button" className="mvp-company mvp-company-open" onClick={() => openDetails(item)}>
                      <CompanyMark item={item} />
                      <span className="mvp-company-name">
                        <HoverText text={item.client_name} />
                      </span>
                      <svg className="mvp-row-chevron" xmlns="http://www.w3.org/2000/svg" width="11" height="19" viewBox="0 0 11 19" fill="none">
                        <path d="M0.423828 18.4238L9.42383 9.42383L0.423827 0.423828" stroke-width="1.2" stroke="#405BFF" />
                      </svg>
                    </button>
                  </td>
                  <td className="mvp-col-since">
                    <HoverText text={item.created_at ? moment(item.created_at).format("MMMM DD, YYYY") : "—"} />
                  </td>
                  <td className="mvp-col-contact">
                    <HoverText text={contactName(item)} />
                  </td>
                  <td className="mvp-col-email">
                    <HoverText text={contactEmail(item)} />
                  </td>
                  <td className="mvp-col-status">
                    <span className={item.is_active ? "mvp-status-pill is-active" : "mvp-status-pill is-inactive"}>
                      {item.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="mvp-col-details">
                    <button type="button" className="mvp-details-btn" onClick={() => openDetails(item)}>
                      Details
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="mvp-pagination">
          {pageItems(currentPage, pageCount).map((item) =>
            typeof item === "string" ? (
              <span key={item} className="mvp-page-ellipsis">
                ...
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={item === currentPage ? "is-current" : ""}
                onClick={() => setPage(item)}
              >
                {item}
              </button>
            )
          )}
          <button
            type="button"
            className="mvp-page-next"
            disabled={currentPage >= pageCount}
            onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
          >
            Next
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M4.2 2.4 7.8 6 4.2 9.6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
